import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../API/axiosInstance";
import { Check, X } from "lucide-react";
import { generateHash } from "../API/hashUtil";
import "./../styles/Signin.css";
import "./../styles/gradient.css";
import "../styles/DecoyLogin.css";
import "./../styles/NewSignin.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PageTemplate from "./CommonPage/PageTemplate";

// Password policy: 8-64 chars, uppercase, lowercase, digit, special char
// Must match backend PasswordResetController.PASSWORD_PATTERN
export const PASSWORD_REGEX =
  /^(?!.*<script>)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,64}$/;

export default function ResetPasswordPage() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [tokenFromUrl, setTokenFromUrl] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Disable right-click
  useEffect(() => {
    const disableRightClick = (e) => e.preventDefault();
    document.addEventListener("contextmenu", disableRightClick);
    return () => document.removeEventListener("contextmenu", disableRightClick);
  }, []);

  // Validate token on mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");

    if (!token) {
      setError("No reset token provided");
      setIsLoading(false);
      navigate("/password/forget");
      return;
    }

    // Basic token format validation before making API call
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(token)) {
      setError("Invalid token format");
      setIsLoading(false);
      navigate("/password/forget");
      return;
    }

    const validateToken = async () => {
      try {
        // Use api (axios) — baseURL already set, no need to prepend apiUrl
        const response = await api.get("/password/checktoken", {
          params: { token },
        });

        if (response.data === true) {
          setIsTokenValid(true);
          setTokenFromUrl(token);
        } else {
          setError("Invalid or expired reset token");
          navigate("/password/forget");
        }
      } catch (err) {
        setError("Error validating reset token");
        navigate("/password/forget");
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, [location.search, navigate]);

  const handleResetLink = async (event) => {
    event.preventDefault();
    setError("");

    // Prevent double submission
    if (isSubmitting) return;

    // Validate password strength on frontend
    if (!PASSWORD_REGEX.test(newPassword)) {
      setError("Password does not meet strength requirements");
      return;
    }

    // Check passwords match
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Check max length
    if (newPassword.length > 64) {
      setError("Password must not exceed 64 characters");
      return;
    }

    // Ensure token exists
    if (!tokenFromUrl) {
      setError("Reset token is missing. Please request a new link.");
      return;
    }

    setIsSubmitting(true);

    const stringParams = {
      tokenurl: tokenFromUrl.toString(),
      password: newPassword.toString(),
    };
    const hexHash = generateHash(stringParams);

    try {
      await api.post("/password/reset", {
        tokenurl: tokenFromUrl,
        password: newPassword,
        HPPcode: hexHash,
      });

      // Clear password fields immediately after success
      setNewPassword("");
      setConfirmPassword("");
      setSuccessDialogOpen(true);
    } catch (err) {
      if (err.response?.status === 400) {
        setError(
          err.response?.data || "Invalid request. The token may have expired.",
        );
      } else if (err.response?.status === 429) {
        setError("Too many attempts. Please try again later.");
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    navigate("/");
  };

  // Loading state
  if (isLoading && !isTokenValid) {
    return (
      <PageTemplate>
        <div className="signin__loading">Validating reset token...</div>
      </PageTemplate>
    );
  }

  // Invalid token state
  if (!isTokenValid && !isLoading) {
    return (
      <PageTemplate>
        <div className="signin__error-container">
          <p>Access Denied: Invalid Token</p>
          <button
            className="signin__button"
            onClick={() => navigate("/password/forget")}
          >
            Request New Token
          </button>
        </div>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate>
      <h2 className="signin__title">Reset Password</h2>
      <p className="signin__subtitle">
        Create a new secure password for your account
      </p>

      {error && <div className="signin__error-message">{error}</div>}

      <form className="signin__form" onSubmit={handleResetLink}>
        <div className="signin__input-group">
          <label htmlFor="new-password" className="signin__label">
            New Password
          </label>
          <div
            className="signin__input-wrapper"
            style={{ position: "relative" }}
          >
            <input
              className="signin__input"
              type={showNewPassword ? "text" : "password"}
              id="new-password"
              name="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              required
              disabled={isSubmitting}
              autoComplete="new-password"
              maxLength={64}
              style={{ paddingRight: "40px" }}
            />
            <button
              type="button"
              onClick={() => setShowNewPassword((prev) => !prev)}
              className="signin__password-toggle"
              aria-label={showNewPassword ? "Hide password" : "Show password"}
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#666",
                padding: "0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "20px",
                height: "20px",
                zIndex: 1,
              }}
            >
              <FontAwesomeIcon
                icon={showNewPassword ? faEyeSlash : faEye}
                className="password-toggle-icon"
              />
            </button>
          </div>
          <PasswordStrengthMeter password={newPassword} />
        </div>

        <div className="signin__input-group">
          <label htmlFor="confirm-password" className="signin__label">
            Confirm Password
          </label>
          <div
            className="signin__input-wrapper"
            style={{ position: "relative" }}
          >
            <input
              className="signin__input"
              type={showConfirmPassword ? "text" : "password"}
              id="confirm-password"
              name="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              disabled={isSubmitting}
              autoComplete="new-password"
              maxLength={64}
              style={{ paddingRight: "40px" }}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              className="signin__password-toggle"
              aria-label={
                showConfirmPassword ? "Hide password" : "Show password"
              }
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#666",
                padding: "0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "20px",
                height: "20px",
                zIndex: 1,
              }}
            >
              <FontAwesomeIcon
                icon={showConfirmPassword ? faEyeSlash : faEye}
                className="password-toggle-icon"
              />
            </button>
          </div>
          {confirmPassword && (
            <PasswordMatchIndicator
              password={confirmPassword}
              comparePassword={newPassword}
            />
          )}
        </div>

        <button
          type="submit"
          className={`signin__button ${
            isSubmitting ? "signin__button--submitting" : ""
          }`}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Resetting..." : "Reset Password"}
        </button>

        <div className="signin__link-container">
          <p>
            Remember your password? <a href="/">Sign In</a>
          </p>
        </div>
      </form>

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            position: "fixed",
            top: "1%",
            margin: "0 auto",
            width: "400px",
            maxWidth: "90%",
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <CheckCircleIcon color="success" /> Success
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Password changed successfully. You will be redirected to the login
            page.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessDialogClose} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </PageTemplate>
  );
}

function PasswordStrengthMeter({ password }) {
  const [strength, setStrength] = useState({
    score: 0,
    label: "",
    color: "#e9ecef",
    requirements: {
      uppercase: false,
      lowercase: false,
      number: false,
      special: false,
      minLength: false,
    },
  });

  useEffect(() => {
    if (!password) {
      setStrength({
        score: 0,
        label: "",
        color: "#e9ecef",
        requirements: {
          uppercase: false,
          lowercase: false,
          number: false,
          special: false,
          minLength: false,
        },
      });
      return;
    }

    const requirements = {
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[^A-Za-z0-9]/.test(password),
      minLength: password.length >= 8,
    };

    const meetsCount = Object.values(requirements).filter(Boolean).length;

    let label = "Weak";
    let color = "#ff4d4d";

    if (meetsCount === 5) {
      label = "Strong";
      color = "#28a745";
    } else if (meetsCount >= 3) {
      label = "Medium";
      color = "#ffc107";
    }

    setStrength({ score: meetsCount, label, color, requirements });
  }, [password]);

  const progressWidth = (strength.score / 5) * 100;

  const renderRequirementItem = (isValid, text) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "8px 12px",
        backgroundColor: isValid
          ? "rgba(40, 167, 69, 0.1)"
          : "rgba(255, 77, 77, 0.1)",
        borderRadius: "4px",
        marginBottom: "6px",
      }}
    >
      <div
        style={{
          backgroundColor: isValid ? "#28a745" : "#ff4d4d",
          borderRadius: "50%",
          width: "20px",
          height: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginRight: "8px",
          flexShrink: 0,
        }}
      >
        {isValid ? (
          <Check size={14} color="white" />
        ) : (
          <X size={14} color="white" />
        )}
      </div>
      <span
        style={{
          fontSize: "14px",
          color: isValid ? "#28a745" : "#ff4d4d",
          fontWeight: "500",
        }}
      >
        {text}
      </span>
    </div>
  );

  return (
    <div style={{ marginTop: "16px", width: "100%" }}>
      <div
        style={{
          position: "relative",
          height: "6px",
          backgroundColor: "#e9ecef",
          borderRadius: "3px",
          marginBottom: "8px",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: `${progressWidth}%`,
            backgroundColor: strength.color,
            borderRadius: "3px",
            transition: "width 0.3s ease",
          }}
        />
      </div>

      {strength.label && (
        <div
          style={{
            textAlign: "right",
            fontSize: "14px",
            color: "#6c757d",
            marginBottom: "16px",
          }}
        >
          {strength.label}
        </div>
      )}

      <div style={{ marginTop: "8px" }}>
        {renderRequirementItem(
          strength.requirements.minLength,
          "Minimum 8 Characters",
        )}
        {renderRequirementItem(
          strength.requirements.uppercase,
          "Uppercase Letter",
        )}
        {renderRequirementItem(
          strength.requirements.lowercase,
          "Lowercase Letter",
        )}
        {renderRequirementItem(strength.requirements.number, "Number")}
        {renderRequirementItem(
          strength.requirements.special,
          "Special Character",
        )}
      </div>
    </div>
  );
}

function PasswordMatchIndicator({ password, comparePassword }) {
  if (!password || !comparePassword) return null;

  const passwordsMatch = password === comparePassword;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        marginTop: "0.5rem",
        color: passwordsMatch ? "#28a745" : "#ff4d4d",
        fontSize: "0.8rem",
      }}
    >
      {passwordsMatch ? (
        <Check size={16} style={{ marginRight: "5px" }} />
      ) : (
        <X size={16} style={{ marginRight: "5px" }} />
      )}
      {passwordsMatch ? "Passwords match" : "Passwords do not match"}
    </div>
  );
}
